import React, { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import {
  AddCampaignDetailsFormValues,
  BonusCampaignsDto,
} from '@/api/Wallet/Types/MarketingBonusCampaignDto'
import { Modal } from '@/components/CustomModal'
import { Loading } from '@/components/Loading'
import { Spinner } from '@/components/Spinner'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { useAppDispatch, useAppSelector } from '@/store'
import { pageableSettings } from '@/store/grid/grid.slice'
import { fetchTickmillCompaniesAction } from '@/store/identity/identity.actions'
import {
  fetchBonusAvailabilityPeriodsAction,
  fetchBonusCampaignCountdownTriggersAction,
  fetchBonusCampaignExcludeTypesAction,
  fetchBonusLabelsAction,
  getAllowedBonusTypesAction,
} from '@/store/wallet/wallet-actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import { FilterType } from 'components/Filter'
import { getBonusCampaignFilterFields } from 'components/Filter/BonusCampaignFields/bonusCampaignFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { SelectIntroducingBrokerModalOpenEvent } from 'components/Filter/IntroducingBrokersFields/SelectIntroducingBrokersFilter'
import { Link } from 'components/Link'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { IntroducingBrokersModal } from 'routes/Records/IB/IntroducingBrokersModal'
import { SelectIntroducingBrokerEvent } from 'routes/Records/IB/IntroducingBrokersTable'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'
import { PageQuery } from 'utils/api'
import { links } from 'utils/featureToggles'
import { buildQuery } from 'utils/QueryHelpers'

import AddButton from '../../../components/AddButton/AddButton'
import { AddBonusCampaignDetailsForm } from './AddBonusCampaignDetailsForm'
import { bonusCampaignColumn } from './bonusCampaignColumn'

const BonusCampaignModulePage = (): JSX.Element => {
  const crumbs = [{ title: 'Bonus Schemes', link: links.bonusModule.url }]
  const walletsApiClient = useApiClient(WalletsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const [isIntroducingBrokersModalOpen, setIntroducingBrokersModalOpen] = useState<boolean>(false)
  const [introducingBrokerFilterId, setIntroducingBrokerFilterId] = useState<string>()

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const dispatch = useAppDispatch()
  const setToast = useContext(ToastContext)

  const [bonuses, setBonuses] = useState<BonusCampaignsDto[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [tableData, setTableData] = useState<{
    skipCount?: boolean
    meta?: PageData<BonusCampaignsDto[]>
  }>({
    skipCount: true,
  })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const gridFilterByFilterIdSelector = useAppSelector(selectGridFilterByFilterId)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const bonusTypes = useAppSelector((state) => state.wallet.bonusTypes)
  const bonusLabels = useAppSelector((state) => state.wallet.bonusLabels)
  const bonusCampaignExcludeTypes = useAppSelector(
    (state) => state.wallet.bonusCampaignExcludeTypes
  )
  const bonusCampaignAvialabilityPeriods = useAppSelector(
    (state) => state.wallet.bonusCampaignAvailabilityPeriods
  )
  const bonusCampaignCountdownTriggers = useAppSelector(
    (state) => state.wallet.bonusCampaignCountdownTriggers
  )

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<BonusCampaignsDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<BonusCampaignsDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const response = await walletsApiClient.getBonusCampaigns({
            search: searchQuery,
            pageIndex: gridFilterState.pageIndex || 1,
            pageSize: gridFilterState.pageSize || 10,
          })

          if (isEmpty(bonusCampaignCountdownTriggers)) {
            dispatch(fetchBonusCampaignCountdownTriggersAction(walletsApiClient))
          }
          if (isEmpty(bonusCampaignAvialabilityPeriods)) {
            dispatch(fetchBonusAvailabilityPeriodsAction(walletsApiClient))
          }
          if (isEmpty(bonusCampaignExcludeTypes)) {
            dispatch(fetchBonusCampaignExcludeTypesAction(walletsApiClient))
          }
          if (isEmpty(bonusLabels)) {
            dispatch(fetchBonusLabelsAction(walletsApiClient))
          }
          if (isEmpty(bonusTypes)) {
            dispatch(getAllowedBonusTypesAction(walletsApiClient))
          }
          if (isEmpty(tickmillCompanies)) {
            dispatch(fetchTickmillCompaniesAction(identityApiClient))
          }

          if (response?.items) {
            setBonuses(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getBonusCampaigns({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setBonuses(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSearchQuery, gridActions, gridState.pageSize, gridState.pageIndex, walletsApiClient]
  )

  const handleSubmit = async (data: AddCampaignDetailsFormValues) => {
    await walletsApiClient.addBonusCampaign(data)
    setToast(successToast('Marketing Bonus Campaign added successfully.'))
    setIsModalVisible(false)
    await filterFetch()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pageSize = take
    const pageIndex = skip / take + 1
    const searchQuery = getSearchQuery()

    gridActions.setGridFilters({
      pageIndex,
      skip,
      take: pageSize,
      pageSize,
    })

    const response = await walletsApiClient.getBonusCampaigns({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
      skip,
      take: pageSize,
    })

    if (response?.items) {
      setBonuses(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleIntroducingBrokersModalOpen = (event: SelectIntroducingBrokerModalOpenEvent) => {
    setIntroducingBrokersModalOpen(true)
    setIntroducingBrokerFilterId(event.filterId)
  }
  const handleIntroducingBrokersSelect = (event: SelectIntroducingBrokerEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }
  const handleIntroducingBrokersModalClose = () => {
    setIntroducingBrokersModalOpen(false)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isIntroducingBrokersModalOpen && (
        <IntroducingBrokersModal
          filterId={introducingBrokerFilterId}
          onSelect={handleIntroducingBrokersSelect}
          onClose={handleIntroducingBrokersModalClose}
        />
      )}
      {isModalVisible && (
        <Modal visible={isModalVisible} onClose={() => setIsModalVisible(false)}>
          <AddBonusCampaignDetailsForm
            tickmillCompanies={tickmillCompanies}
            bonusTypes={bonusTypes}
            bonusLabels={bonusLabels}
            bonusCampaignExcludeTypes={bonusCampaignExcludeTypes}
            bonusCampaignAvialabilityPeriods={bonusCampaignAvialabilityPeriods}
            bonusCampaignCountdownTriggers={bonusCampaignCountdownTriggers}
            onSubmit={handleSubmit}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <Loading isLoading={gridStatusState.isLoading}>
        <SelectFiltersFacade
          gridState={gridState}
          gridStatusActions={gridStatusActions}
          gridStatusState={gridStatusState}
          setGridFilters={gridActions.setGridFilters}
          filterFetch={(params) => filterFetch(params)}
          filterType={FilterType.BonusCampaignOverview}
          filterFields={getBonusCampaignFilterFields(handleIntroducingBrokersModalOpen)}
          containerClassName='campaign-table__filter'
          getQuery={getSearchQuery}
        />

        <AddButton
          text='Add a Bonus'
          disabled={!hasPermission(permissions, PermissionsEnum.CanEditBonusCampaigns)}
          onClick={() => setIsModalVisible(true)}
          data-test='add-bonus-button'
        />
        <Grid
          data={bonuses}
          take={gridState.take || 10}
          skip={gridState.skip || 0}
          total={gridState.total || 0}
          onPageChange={handleGridPageChange}
          pageable={pageableSettings}
        >
          <Column
            title='Name'
            width={200}
            cell={(gridCellProps) => {
              if (!gridCellProps.dataItem?.name) {
                return <td />
              }
              if (!hasPermission(permissions, PermissionsEnum.CanViewBonusCampaigns)) {
                return <td>{gridCellProps.dataItem.name}</td>
              }
              return (
                <td data-test='name-column'>
                  <Link
                    to={{
                      pathname: generatePath(links.bonusModule.detailsUrl as string, {
                        id: gridCellProps.dataItem.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {gridCellProps.dataItem.name}
                  </Link>
                </td>
              )
            }}
          />
          {bonusCampaignColumn
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </Loading>
    </React.Fragment>
  )
}

export default BonusCampaignModulePage
